import { zodResolver } from '@hookform/resolvers/zod'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useFormTemplates } from '@/hooks/useForm'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { createForm } from '@/services/api/form.service'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { parseApiError } from '@/utils/error-parser'


const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  parentRecordGroupId: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export function NewRecordGroupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { recordGroups, create, isCreating } = useRecordGroups()
  const lockedResourceIds = useSubscriptionStore((state) => state.lockedResourceIds)
  const { data: templates = [] } = useFormTemplates()
  const [templateId, setTemplateId] = useState('none')
  // Covers the form request that follows the group's, so a second click can't create a second group.
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const parentId = watch('parentRecordGroupId')
  const [parentFromUrl, setParentFromUrl] = useState<string | null>(null)

  // Read parent ID from URL query parameter
  useEffect(() => {
    const parent = searchParams.get('parent')
    if (parent) {
      setValue('parentRecordGroupId', parent)
      setParentFromUrl(parent)
    }
  }, [searchParams, setValue])

  const onSubmit = (data: FormData) => {
    setSubmitting(true)
    create(
      {
        name: data.name,
        parentRecordGroupId: data.parentRecordGroupId || undefined,
      },
      {
        onSuccess: async (newGroup) => {
          toast.success('Record group created')
          const template = templates.find((t) => t.id === templateId)
          if (template) {
            // The same call the Form Designer makes, so the form is validated exactly like a hand-built one.
            try {
              await createForm({
                recordGroupId: newGroup.id,
                mainFieldKey: template.mainFieldKey,
                fields: template.fields.map((f, index) => ({
                  key: f.key, label: f.label, type: f.type, required: f.required,
                  options: f.options, validations: f.validations, orderIndex: index,
                })),
              })
            } catch (error) {
              toast.error('The group was created, but its form could not be set up', { description: parseApiError(error).detail || undefined })
              navigate(`/record-groups/${newGroup.id}/form`)
              return
            }
          }
          navigate(`/record-groups/${newGroup.id}`)
        },
        onError: () => {
          setSubmitting(false)
          toast.error('Failed to create record group')
        },
      }
    )
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Create Record Group</CardTitle>
          <CardDescription>
            Define a new entity type for your workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="e.g., Customers, Orders, Products"
                {...register('name')}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive-600">{errors.name.message}</p>
              )}
            </div>

            {parentFromUrl ? (
              <div className="space-y-2">
                <Label>Parent Group</Label>
                <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
                  {recordGroups.find((g) => g.id === parentFromUrl)?.name || 'Loading...'}
                </div>
                <p className="text-sm text-neutral-500">
                  Creating as a child of the selected parent
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="parent">Parent Group (Optional)</Label>
                <Select
                  value={parentId || ''}
                  onValueChange={(value) => {
                    setValue('parentRecordGroupId', value === 'none' ? undefined : value)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select parent group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No parent</SelectItem>
                    {recordGroups.map((group) => (
                      <SelectItem
                        key={group.id}
                        value={group.id}
                        disabled={lockedResourceIds.has(group.id)}
                      >
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-neutral-500">
                  Select a parent to create a hierarchical structure
                </p>
              </div>
            )}

            {templates.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="template">Start from a template (optional)</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger id="template">
                    <SelectValue placeholder="No template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template: start with an empty form</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} ({template.fields.length} fields)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-neutral-500">
                  The template's fields are copied into the group's form. You can change them in the Form Designer.
                </p>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/record-groups')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating || submitting}>
                {isCreating || submitting ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}